import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useState, useEffect } from "react";
import axios from "axios";
import { ShoppingCart, User, Menu, X, Crown, Users, MessageSquare, Settings, Phone, Mail, MapPin, Truck, ShieldCheck, Repeat } from "lucide-react";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence } from "framer-motion";

const SEO_MAP = {
  "/": {
    title: "AM GOLD | Premium Gold & Silver Coins Online in India",
    description: "Buy 24K gold and 999 fine silver coins at live market prices. BIS-hallmarked Lakshmi coins, free shipping across India, KYC-secure checkout."
  },
  "/products/gold": {
    title: "Gold Coins | AM GOLD",
    description: "Shop 24K BIS-hallmarked gold Lakshmi coins from 100mg to 8g at live market prices with free shipping across India."
  },
  "/products/silver": {
    title: "Silver Coins | AM GOLD",
    description: "Shop 999 fine silver Lakshmi coins from 5g to 100g at live market prices with free shipping across India."
  },
  "/cart": {
    title: "Your Cart | AM GOLD",
    description: "Review your gold and silver coin selection before secure checkout."
  },
  "/checkout": {
    title: "Secure Checkout | AM GOLD",
    description: "Complete your purchase with KYC-secure checkout and UPI or bank payment options."
  },
  "/dashboard": { title: "My Account | AM GOLD", description: "View your orders and account details." },
  "/affiliate": { title: "Affiliate Dashboard | AM GOLD", description: "Track your referrals and commission earnings." },
  "/forum": { title: "Partner Forum | AM GOLD", description: "Community space for AM GOLD affiliate partners." }
};

export const PageSeo = () => {
  const { pathname } = useLocation();
  let meta = SEO_MAP[pathname];
  if (!meta) {
    if (pathname.startsWith("/product/")) {
      meta = { title: "Coin Details | AM GOLD", description: "View live price, weight, purity and hallmark details for this gold or silver coin." };
    } else {
      meta = { title: "AM GOLD | Gold & Silver Coins", description: "Premium gold and silver coins with live market pricing." };
    }
  }
  return (
    <>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="AM GOLD" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
    </>
  );
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [prices, setPrices] = useState({ gold: 0, silver: 0 });

  useEffect(() => {
    fetchPrices();
    if (user) fetchCart();
    const interval = setInterval(fetchPrices, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [user]);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/prices/live`);
      setPrices({
        gold: response.data.gold?.rate_per_gram || 0,
        silver: response.data.silver?.rate_per_gram || 0
      });
    } catch (error) {
      console.error("Failed to fetch prices:", error);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      const total = response.data.items.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(total);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Price Marquee */}
      <div className="marquee-container" data-testid="price-marquee">
        <Marquee speed={30} gradient={false}>
          <div className="flex items-center gap-12 px-4">
            <span className="text-sm text-[#A3A3A3]">
              <span className="text-[#D4AF37] font-semibold">GOLD</span> ₹{prices.gold.toLocaleString()}/g
            </span>
            <span className="text-[#737373]">|</span>
            <span className="text-sm text-[#A3A3A3]">
              <span className="text-[#C0C0C0] font-semibold">SILVER</span> ₹{prices.silver.toLocaleString()}/g
            </span>
            <span className="text-[#737373]">|</span>
            <span className="text-xs text-[#737373]">Live rates from Priyanka Bullion • Updated every 2 mins</span>
            <span className="text-[#737373] mx-8">|</span>
          </div>
        </Marquee>
      </div>

      {/* Main Navigation */}
      <nav className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-md border-b border-[rgba(255,255,255,0.05)]" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#050505]" />
              </div>
              <span className="font-['Playfair_Display'] text-xl font-bold text-white">AM GOLD</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/products/gold" className={`nav-link ${isActive('/products/gold') ? 'active' : ''}`} data-testid="nav-gold">
                Gold Coins
              </Link>
              <Link to="/products/silver" className={`nav-link ${isActive('/products/silver') ? 'active' : ''}`} data-testid="nav-silver">
                Silver Coins
              </Link>
              {user?.is_affiliate && (
                <>
                  <Link to="/affiliate" className={`nav-link ${isActive('/affiliate') ? 'active' : ''}`} data-testid="nav-affiliate">
                    Affiliate
                  </Link>
                  <Link to="/forum" className={`nav-link ${isActive('/forum') ? 'active' : ''}`} data-testid="nav-forum">
                    Forum
                  </Link>
                </>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin" className={`nav-link ${isActive('/admin') ? 'active' : ''}`} data-testid="nav-admin">
                  Admin
                </Link>
              )}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <Link to="/cart" className="relative p-2" data-testid="cart-link">
                <ShoppingCart className="w-5 h-5 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors" />
                {cartCount > 0 && (
                  <span className="cart-badge" data-testid="cart-count">{cartCount}</span>
                )}
              </Link>

              {user ? (
                <div className="hidden md:flex items-center gap-4">
                  <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors" data-testid="user-menu">
                    <User className="w-4 h-4" />
                    <span>{user.name}</span>
                  </Link>
                  <button onClick={handleLogout} className="text-sm text-[#737373] hover:text-[#D4AF37] transition-colors" data-testid="logout-btn">
                    Logout
                  </button>
                </div>
              ) : (
                <Link to="/login" className="btn-gold text-sm py-2 px-4" data-testid="login-btn">
                  Login
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#121212] border-t border-[rgba(255,255,255,0.05)]"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-4 space-y-2">
                <Link to="/products/gold" className="block py-2 text-[#A3A3A3] hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                  Gold Coins
                </Link>
                <Link to="/products/silver" className="block py-2 text-[#A3A3A3] hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                  Silver Coins
                </Link>
                {user?.is_affiliate && (
                  <>
                    <Link to="/affiliate" className="block py-2 text-[#A3A3A3] hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                      Affiliate Dashboard
                    </Link>
                    <Link to="/forum" className="block py-2 text-[#A3A3A3] hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                      Forum
                    </Link>
                  </>
                )}
                {user?.role === 'admin' && (
                  <Link to="/admin" className="block py-2 text-[#A3A3A3] hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                    Admin Dashboard
                  </Link>
                )}
                {user ? (
                  <>
                    <Link to="/dashboard" className="block py-2 text-[#A3A3A3] hover:text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                      My Account
                    </Link>
                    <button onClick={handleLogout} className="block py-2 text-[#737373] hover:text-[#D4AF37]">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/login" className="block py-2 text-[#D4AF37]" onClick={() => setMobileMenuOpen(false)}>
                    Login / Register
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0a0a0a] border-t border-[rgba(255,255,255,0.08)] mt-auto" data-testid="footer">
      {/* Trust strip */}
      <div className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3" data-testid="trust-shipping">
            <Truck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
            <span className="text-sm text-[#CFCFCF]">Free Insured Shipping</span>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-hallmark">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
            <span className="text-sm text-[#CFCFCF]">BIS Hallmarked Coins</span>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-buyback">
            <Repeat className="w-5 h-5 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
            <span className="text-sm text-[#CFCFCF]">Live-Rate Buyback</span>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-kyc">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
            <span className="text-sm text-[#CFCFCF]">KYC-Secure Checkout</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand + Contact */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#050505]" aria-hidden="true" />
              </div>
              <span className="font-['Playfair_Display'] text-xl font-bold text-white">AM GOLD</span>
            </div>
            <p className="text-sm text-[#B0B0B0] mb-4">
              Premium gold and silver coins with real-time pricing. Trusted by thousands across India.
            </p>
            <ul className="space-y-2 text-sm text-[#B0B0B0]" data-testid="footer-contact">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>AM Gold &amp; Silver, Madurai, Tamil Nadu 625001</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
                <a href="tel:+919843223852" className="hover:text-[#D4AF37]">+91 98432 23852</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
                <a href="mailto:info@amgold.in" className="hover:text-[#D4AF37]">info@amgold.in</a>
              </li>
              <li className="text-xs text-[#9A9A9A] pt-1">GSTIN: Available on request</li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Products</h4>
            <ul className="space-y-2">
              <li><Link to="/products/gold" className="text-sm text-[#B0B0B0] hover:text-[#D4AF37]">Gold Coins</Link></li>
              <li><Link to="/products/silver" className="text-sm text-[#B0B0B0] hover:text-[#D4AF37]">Silver Coins</Link></li>
              <li><Link to="/dashboard" className="text-sm text-[#B0B0B0] hover:text-[#D4AF37]">My Orders</Link></li>
              <li><Link to="/cart" className="text-sm text-[#B0B0B0] hover:text-[#D4AF37]">Cart</Link></li>
            </ul>
          </div>

          {/* Buyback policy */}
          <div className="md:col-span-2" data-testid="footer-buyback-policy">
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Buyback Policy</h4>
            <p className="text-sm text-[#B0B0B0] leading-relaxed">
              We buy back AM GOLD coins at the prevailing live market rate (based on daily Priyanka Bullion rates),
              less applicable charges. Please retain your original invoice and tamper-sealed packaging for the best buyback value.
            </p>
          </div>
        </div>

        <div className="border-t border-[rgba(255,255,255,0.08)] mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#9A9A9A]">
            © 2026 AM GOLD. All rights reserved. | Prices linked to live market data from Priyanka Bullion.
          </p>
          <div className="flex gap-4 text-xs text-[#9A9A9A]">
            <span>Terms &amp; Conditions</span>
            <span aria-hidden="true">|</span>
            <span>Privacy Policy</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <PageSeo />
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};
